import { createReducer } from '@reduxjs/toolkit';
import { ReturnOrderDataDto, ReturnOrderDto } from '../../api/ClientApi';

import {
    saveReturnOrder,
    importReturnOrders,
    addOrderGoodsItemToReturnOrder,
    removeOrderGoodsItemFromReturnOrder,
    addOrUpdateReturnOrder,
} from '../actions/returnOrderActions';
import { cancelReturnOrder, closeReturnOrder } from '../actions/orderActions';

export interface ReturnOrdersState {
    returnOrders: ReturnOrderDto[] | null;
    returnOrdersInfoDate: Date | null;
    returnOrdersFetchActive: boolean;
}

export const returnOrdersReducer = createReducer<ReturnOrdersState>(
    { returnOrders: null, returnOrdersInfoDate: null, returnOrdersFetchActive: false },
    (builder) => {
        builder
            .addCase(addOrderGoodsItemToReturnOrder, (state: ReturnOrdersState, action: any) => {
                const { returnOrderId, returnOrderGoodsItem } = action.payload;

                if (!returnOrderGoodsItem || !state.returnOrders) {
                    return;
                }

                const returnOrderIndex = state.returnOrders?.findIndex((o) => o.id === returnOrderId);
                const returnOrderGoodsItemId = returnOrderGoodsItem.id;

                if (returnOrderIndex !== undefined && returnOrderIndex >= 0) {
                    const oldReturnOrder = state.returnOrders[returnOrderIndex];

                    const oldReturnOrderGoodsItemIndex = oldReturnOrder.orderGoodsItems?.findIndex(
                        (ogi) => ogi.id === returnOrderGoodsItemId,
                    );

                    if (oldReturnOrderGoodsItemIndex !== undefined && oldReturnOrderGoodsItemIndex >= 0) {
                        oldReturnOrder.orderGoodsItems?.splice(oldReturnOrderGoodsItemIndex, 1, returnOrderGoodsItem);
                    } else {
                        if (!oldReturnOrder.orderGoodsItems) {
                            oldReturnOrder.orderGoodsItems = [returnOrderGoodsItem];
                        } else {
                            oldReturnOrder.orderGoodsItems.unshift(returnOrderGoodsItem);
                        }
                    }

                    state.returnOrders.splice(returnOrderIndex, 1, oldReturnOrder);
                }
            })
            .addCase(removeOrderGoodsItemFromReturnOrder, (state: ReturnOrdersState, action: any) => {
                const { returnOrderId, returnOrderGoodsItemId } = action.payload;

                if (!returnOrderGoodsItemId || returnOrderGoodsItemId <= 0 || !state.returnOrders) {
                    return;
                }

                const returnOrderIndex = state.returnOrders?.findIndex((o) => o.id === returnOrderId);

                if (returnOrderIndex !== undefined && returnOrderIndex >= 0) {
                    const oldReturnOrder = state.returnOrders[returnOrderIndex];

                    const oldReturnOrderGoodsItemIndex = oldReturnOrder.orderGoodsItems?.findIndex(
                        (ogi) => ogi.id === returnOrderGoodsItemId,
                    );

                    if (oldReturnOrderGoodsItemIndex !== undefined && oldReturnOrderGoodsItemIndex >= 0) {
                        oldReturnOrder.orderGoodsItems?.splice(oldReturnOrderGoodsItemIndex, 1);

                        state.returnOrders.splice(returnOrderIndex, 1, oldReturnOrder);
                    }
                }
            })
            .addCase(saveReturnOrder.fulfilled, (state: ReturnOrdersState, action: any) => {
                if (state.returnOrders === null) {
                    return;
                }

                const payload: ReturnOrderDto = action.payload;

                const returnOrder: ReturnOrderDto = new ReturnOrderDto();
                returnOrder.init(payload);

                const returnOrderIndex = state.returnOrders!.findIndex((a) => {
                    return a.id === returnOrder.id;
                });

                if (returnOrderIndex >= 0) {
                    // update returnOrder
                    state.returnOrders.splice(returnOrderIndex, 1, returnOrder);
                } else {
                    // add returnOrder
                    state.returnOrders.unshift(returnOrder);
                }
            })
            .addCase(cancelReturnOrder.fulfilled, (state: ReturnOrdersState, action: any) => {
                if (state.returnOrders === null) {
                    return;
                }

                const payload: ReturnOrderDto = action.payload;

                const order: ReturnOrderDto = new ReturnOrderDto();
                order.init(payload);

                const orderIndex = state.returnOrders!.findIndex((a) => {
                    return a.id === order.id;
                });

                if (orderIndex >= 0) {
                    // update order
                    state.returnOrders.splice(orderIndex, 1, order);
                } else {
                    // add order
                    state.returnOrders.unshift(order);
                }
            })
            .addCase(closeReturnOrder.fulfilled, (state: ReturnOrdersState, action: any) => {
                if (state.returnOrders === null) {
                    return;
                }

                const payload: ReturnOrderDto = action.payload;

                const order: ReturnOrderDto = new ReturnOrderDto();
                order.init(payload);

                const orderIndex = state.returnOrders!.findIndex((a) => {
                    return a.id === order.id;
                });

                if (orderIndex >= 0) {
                    // update order
                    state.returnOrders.splice(orderIndex, 1, order);
                } else {
                    // add order
                    state.returnOrders.unshift(order);
                }
            })
            .addCase(addOrUpdateReturnOrder, (state: ReturnOrdersState, action: any) => {
                const newReturnOrder = action.payload;

                const oldReturnOrderIndex = state.returnOrders?.findIndex((o) => o.id === newReturnOrder.id);

                if (oldReturnOrderIndex !== undefined && oldReturnOrderIndex >= 0) {
                    state.returnOrders?.splice(oldReturnOrderIndex, 1, newReturnOrder);
                } else {
                    if (!state.returnOrders) {
                        state.returnOrders = [newReturnOrder];
                    } else {
                        state.returnOrders.unshift(newReturnOrder);
                    }
                }
            })
            .addCase(importReturnOrders.fulfilled, (state: ReturnOrdersState, action: any) => {
                const result: ReturnOrderDataDto = action.payload;

                const newReturnOrders = result.orders;

                state.returnOrders = newReturnOrders?.sort((a, b) => (b.id ?? 0) - (a.id ?? 0)) ?? null;
                state.returnOrdersInfoDate = result.infoDate ?? null;

                state.returnOrdersFetchActive = false;
            })
            .addCase(importReturnOrders.pending, (state: ReturnOrdersState, action: any) => {
                state.returnOrdersFetchActive = true;
            })
            .addCase(importReturnOrders.rejected, (state: ReturnOrdersState, action: any) => {
                state.returnOrdersFetchActive = false;
            });
    },
);
